import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';

import { experimentsHydratedAtom, experimentsListParamsAtom } from '@/store';
import { EXPERIMENT_TYPE_OPTIONS } from '@/utils/experiments';

export interface SearchFields {
  name: string;
  description: string;
  domain: string;
  organization: string;
}

export function useExperimentsFilters() {
  const [params, setParams] = useAtom(experimentsListParamsAtom);
  const [, setHydrated] = useAtom(experimentsHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    search: parseAsString.withDefault(''),
    config: parseAsString.withDefault(''),
    status: parseAsString.withDefault('all'),
    types: parseAsString.withDefault(''),
    name: parseAsString.withDefault(''),
    description: parseAsString.withDefault(''),
    domain: parseAsString.withDefault(''),
    organization: parseAsString.withDefault('')
  });

  // Filters state (client UI) mapped to API params (URL synced)
  const [statusFilter, setStatusFilter] = React.useState<string>(queryParams.status);
  const [typesFilter, setTypesFilter] = React.useState<string>(queryParams.types);
  const [searchFields, setSearchFields] = React.useState<SearchFields>({
    name: queryParams.name,
    description: queryParams.description,
    domain: queryParams.domain,
    organization: queryParams.organization
  });

  // Search state
  const [searchTerm, setSearchTerm] = React.useState(queryParams.search);
  const [configSearchTerm, setConfigSearchTerm] = React.useState(queryParams.config);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  // Sync URL parameters with params atom on mount and when URL changes
  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};

    // Build filters from URL parameters
    if (queryParams.search) filters.search = queryParams.search;
    if (queryParams.config) filters.config = queryParams.config;
    if (queryParams.status !== 'all') filters.status = queryParams.status;
    if (queryParams.types) filters.types = queryParams.types;
    if (queryParams.name) filters.name = queryParams.name;
    if (queryParams.description) filters.description = queryParams.description;
    if (queryParams.domain) filters.domain = queryParams.domain;
    if (queryParams.organization) filters.organization = queryParams.organization;

    setParams((prev) => ({
      ...prev,
      page: queryParams.page,
      filters: filters
    }));

    // Mark hydrated after first sync
    setHydrated(true);
  }, [queryParams.page, queryParams.search, queryParams.config, queryParams.status, queryParams.types, queryParams.name, queryParams.description, queryParams.domain, queryParams.organization, setParams]);

  // Sync URL parameters with local state on mount
  React.useEffect(() => {
    setSearchTerm(queryParams.search);
    setConfigSearchTerm(queryParams.config);
    setStatusFilter(queryParams.status);
    setTypesFilter(queryParams.types);
    setSearchFields({
      name: queryParams.name,
      description: queryParams.description,
      domain: queryParams.domain,
      organization: queryParams.organization
    });
  }, []); // Only run on mount

  // Search handler
  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: searchTerm || undefined }
    }));
    setQueryParams({ page: 1, search: searchTerm });
  }, [searchTerm, setParams, setQueryParams]);

  // Clear search handler
  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: undefined }
    }));
    setQueryParams({ page: 1, search: '' });
  }, [setParams, setQueryParams]);

  // Config search handler
  const handleConfigSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, config: configSearchTerm || undefined }
    }));
    setQueryParams({ page: 1, config: configSearchTerm });
  }, [configSearchTerm, setParams, setQueryParams]);

  // Clear config search handler
  const handleClearConfigSearch = React.useCallback(() => {
    setConfigSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, config: undefined }
    }));
    setQueryParams({ page: 1, config: '' });
  }, [setParams, setQueryParams]);

  const applyFilters = React.useCallback(async () => {
    const nextFilters: Record<string, string | undefined> = {
      ...params.filters,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      types: typesFilter || undefined,
      name: searchFields.name || undefined,
      description: searchFields.description || undefined,
      domain: searchFields.domain || undefined,
      organization: searchFields.organization || undefined
    };

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));

    await setQueryParams({
      page: 1,
      status: statusFilter,
      types: typesFilter,
      name: searchFields.name,
      description: searchFields.description,
      domain: searchFields.domain,
      organization: searchFields.organization
    });

    setIsSearchOpen(false);
  }, [params.filters, statusFilter, typesFilter, searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setSearchTerm('');
    setConfigSearchTerm('');
    setStatusFilter('all');
    setTypesFilter('');
    setSearchFields({ name: '', description: '', domain: '', organization: '' });
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      search: '',
      config: '',
      status: 'all',
      types: '',
      name: '',
      description: '',
      domain: '',
      organization: ''
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(() => {
    return (
      Boolean(searchTerm) ||
      Boolean(configSearchTerm) ||
      statusFilter !== 'all' ||
      Boolean(typesFilter) ||
      Boolean(searchFields.name || searchFields.description || searchFields.domain || searchFields.organization)
    );
  }, [searchTerm, configSearchTerm, statusFilter, typesFilter, searchFields]);

  const handleStatusChange = React.useCallback((val: string) => {
    setStatusFilter(val);
    setQueryParams({ page: 1, status: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, status: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handleTypesChange = React.useCallback((val: string) => {
    setTypesFilter(val);
    setQueryParams({ page: 1, types: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, types: val || undefined }
    }));
  }, [setQueryParams, setParams]);

  const handleDomainChange = React.useCallback((val: string) => {
    setSearchFields((prev) => ({ ...prev, domain: val }));
    setQueryParams({ page: 1, domain: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, domain: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    configSearchTerm,
    setConfigSearchTerm,
    statusFilter,
    searchFields,
    setSearchFields,
    hasAnyFilter,
    isSearchOpen,
    setIsSearchOpen,

    // Actions
    handleSearch,
    handleClearSearch,
    handleConfigSearch,
    handleClearConfigSearch,
    applyFilters,
    clearFilters,
    handleStatusChange,
    handleTypesChange,
    handleDomainChange,
    handlePageChange,
    typesFilter,
    typeOptions: EXPERIMENT_TYPE_OPTIONS
  };
}
